import * as SignalR from "@microsoft/signalr";
import EventEmitter from "events";
import _Vue from 'vue';
import axios from "axios";
import {
  LOCALSTORAGE_AUTH2,
  LOCALSTORAGE_USERSSION,
  LOCALSTORAGE_AUTH,
  APICONFIG_HEADER_AUTHORIZATION,
  APICONFIG_HEADER_SESSIONID,
  SERVICE_LINKS,
  WEBAPP_LAYOUT,
  EVENT_BUS
} from "../../common/constants";
import { SOCKET_METHOD } from "../../common/socket.constants";
import { Oauthorization2Response } from "../../utils/authorization/data-model";
import { AuthVerified } from "../../models/generic/auth-transfer";
import store from "../../store";
import { LocalforageWrapper } from "../../utils/localforage-wrapper";
import EventBus from "../../utils/event-bus";
import { isWinAppWebview } from "../../utils/authorization/webview-access-token";

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_OPTIONS = {
  log: true
};

/** Maximum number of token refresh attempts before redirecting to login */
const MAX_AUTH_RETRY_ATTEMPTS = 3;

/** Maximum number of connection retry attempts for non-auth errors */
const MAX_CONNECTION_RETRY_ATTEMPTS = 5;

/** Delay between authentication retry attempts (ms) */
const AUTH_RETRY_DELAY_MS = 2000;

// ============================================================================
// Interfaces
// ============================================================================

interface ISocketConnection {
  options: any;
  connection: any;
  listened: any;
  socket: any;
  toSend: any;
  offline: boolean;
  offEvent: (method: string, callback: any) => void;
  start: (options: any) => Promise<any>;
  onEvent: (method: string, callback: any) => void;
}

// ============================================================================
// SocketConnection Class
// ============================================================================

class SocketConnection extends EventEmitter implements ISocketConnection {
  // ---------------------------------------------------------------------------
  // Properties
  // ---------------------------------------------------------------------------

  options: any;
  connection: any;
  listened: Array<any>;
  socket: any;
  toSend: any;
  offline: boolean;
  pendingMethods: Array<string>;

  private authRetryCount: number;
  private connectionRetryCount: number;
  private isRefreshingToken: boolean;
  private authenticationFailed: boolean;
  private initializing: Promise<boolean>;
  private lastConnectError: any;

  // ---------------------------------------------------------------------------
  // Constructor
  // ---------------------------------------------------------------------------

  constructor(connection: string) {
    super();
    this.connection = connection;
    this.listened = [];
    this.socket = false;
    this.toSend = [];
    this.offline = false;
    this.pendingMethods = [];
    this.authRetryCount = 0;
    this.connectionRetryCount = 0;
    this.isRefreshingToken = false;
    this.authenticationFailed = false;
    this.initializing = null;
    this.lastConnectError = null;
  }

  // ---------------------------------------------------------------------------
  // Public Methods
  // ---------------------------------------------------------------------------

  /**
   * Start the SignalR connection
   */
  async start(options = {}): Promise<void> {
    this.options = Object.assign({}, DEFAULT_OPTIONS, options);
    // OMSEVEN-10852: a token pushed by the WinApp revives a connection that gave up on a revoked one.
    if (isWinAppWebview()) {
      EventBus.$on(EVENT_BUS.AUTH.STATE_CHANGED, () => {
        if (!this.authenticationFailed) return;
        this.authenticationFailed = false;
        this.authRetryCount = 0;
        this._initialize().then(reconnected => { if (reconnected) this.emit("reconnect"); });
      });
    }
    await this._initialize();
  }

  /**
   * Register an event listener for a SignalR method
   */
  onEvent(method: string, callback: any): void {
    console.log('socket onEvent');
    this.listen(method);
    this.on(method, callback);
  }

  /**
   * Listen to a SignalR method
   */
  listen(method: string): void {
    console.log('socket listen');
    if (this.offline) return;
    if (this.listened.some(v => v === method) || this.pendingMethods.some(v => v === method)) return;

    if (this.socket) {
      this.listened.push(method);
      this.handleListenMethod(method);
    } else {
      this.pendingMethods.push(method);
    }
  }

  /**
   * Remove an event listener for a SignalR method
   */
  offEvent(method: string, callback: any): void {
    console.log(`Off listen method: ${method}`);
    this.removeListener(method, callback);

    if (this.socket) {
      if (this.listenerCount(method) <= 0) {
        this.socket.off(method);
        const index = this.listened.findIndex(item => item === method);
        if (index > -1) this.listened.splice(index, 1);
      }
    }
  }

  /**
   * Send a message through SignalR
   */
  send(methodName: string, ...args: any[]): void {
    if (this.options.log) console.log({ type: "send", methodName, args });
    if (this.offline) return;

    if (this.socket) {
      this.socket.send(methodName, ...args);
      return;
    }

    this.once("init", () => this.socket.send(methodName, ...args));
  }

  /**
   * Invoke a SignalR method and wait for response
   */
  async invoke(methodName: string, ...args: any[]): Promise<any> {
    if (this.options.log) console.log({ type: "invoke", methodName, args });
    if (this.offline) return false;

    if (this.socket) {
      return this.socket.invoke(methodName, ...args);
    }

    return new Promise(resolve =>
      this.once("init", () => resolve(this.socket.invoke(methodName, ...args)))
    );
  }

  // ---------------------------------------------------------------------------
  // Connection Management (Private)
  // ---------------------------------------------------------------------------

  /**
   * Initialize the SignalR connection
   * @returns true if connection was successful, false otherwise
   */
  private async _initialize(connection = ""): Promise<boolean> {
    if (this.authenticationFailed) {
      if (this.options.log) console.log("SignalR: Authentication permanently failed, not attempting reconnection");
      return false;
    }

    if (this.connectionRetryCount >= MAX_CONNECTION_RETRY_ATTEMPTS) {
      if (this.options.log) console.log(`SignalR: Max connection retry attempts (${MAX_CONNECTION_RETRY_ATTEMPTS}) exceeded, stopping`);
      return false;
    }

    // A reconnect can be asked for from several places at once - the socket closing,
    // a failed request, a token refresh. Each one used to build its own connection,
    // and every surviving connection delivers the same message again.
    if (this.initializing) return this.initializing;

    let connected: boolean;
    this.initializing = this.connectOnce(connection);
    try {
      connected = await this.initializing;
    } finally {
      this.initializing = null;
    }

    // Recovery runs after the guard is released: it can ask for a new connection
    // itself (a token refresh does), which would otherwise wait on this attempt.
    if (!connected && this.lastConnectError) {
      const error = this.lastConnectError;
      this.lastConnectError = null;
      await this.onConnectionError(error);
    }

    return connected;
  }

  private async connectOnce(connection = ""): Promise<boolean> {
    this.lastConnectError = null;

    try {
      const con = connection || this.connection;
      await this.disposeSocket();

      // Always get the latest token from localStorage before connecting
      const currentToken = this.getLatestAccessToken();
      if (currentToken) {
        this.options.accessToken = currentToken;
      }

      if (this.options.log) {
        console.log("SignalR: Initializing connection with token:", currentToken ? `${currentToken.substring(0, 20)}...` : "NO TOKEN");
      }

      const socket = this.buildHubConnection(con);
      this.setupSocketEventHandlers(socket);

      await socket.start();
      this.onConnectionSuccess(socket);
      return true;
    } catch (error) {
      this.lastConnectError = error;
      return false;
    }
  }

  /**
   * Close the current connection before opening a new one. A connection that is only
   * dereferenced stays open and keeps raising its events, so the app would receive
   * every message once per abandoned connection. The methods it was listening to go
   * back on the pending list, because the next connection starts with none.
   */
  private async disposeSocket(): Promise<void> {
    const socket = this.socket;
    this.socket = false;

    // Queue the methods before checking for a connection to stop: a reconnect that
    // starts from the socket closing has already cleared this.socket, and skipping
    // the queueing there would leave the next connection listening to nothing.
    this.pendingMethods = Array.from(new Set([...this.listened, ...this.pendingMethods]));
    this.listened = [];

    if (!socket) return;

    try {
      socket.onclose = null;
      await socket.stop();
    } catch (error) {
      if (this.options?.log) console.log("SignalR: Could not stop the previous connection", error);
    }
  }

  /**
   * Build the SignalR HubConnection
   */
  private buildHubConnection(url: string): SignalR.HubConnection {
    return new SignalR.HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => this.getLatestAccessToken(),
        skipNegotiation: true,
        transport: SignalR.HttpTransportType.WebSockets
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryAttempt) => {
          if (this.authenticationFailed) {
            return null; // Stop retrying
          }
          return Math.min(30000, Number(retryAttempt) * 5000);
        }
      })
      .configureLogging(SignalR.LogLevel.Information)
      .build();
  }

  /**
   * Setup event handlers for the socket
   */
  private setupSocketEventHandlers(socket: SignalR.HubConnection): void {
    socket.onclose = async (error) => {
      if (this.options.log) console.log("Socket disconnected", error);
      this.socket = false;

      const statusCode = (error as any)?.statusCode;
      if (statusCode === 401) {
        await this.handleAuthenticationError();
        return;
      }

      if (!this.authenticationFailed) {
        const reconnected = await this._initialize();
        if (reconnected) {
          this.emit("reconnect");
        }
      }
    };
  }

  /**
   * Handle successful connection
   */
  private onConnectionSuccess(socket: SignalR.HubConnection): void {
    this.authRetryCount = 0;
    this.connectionRetryCount = 0;
    this.socket = socket;
    this.emit("init");
    this.listenOnPendingMethods();
  }

  /**
   * Handle connection error
   */
  private async onConnectionError(error: any): Promise<void> {
    if (this.options.log) console.log("SignalR connection error:", error);

    const { statusCode } = error as any;
    const errorMessage = error?.message || "";

    // Check for 401 error
    if (statusCode === 401 || errorMessage.includes("401")) {
      await this.handleAuthenticationError();
      return;
    }

    // For non-auth errors, retry with exponential backoff
    this.connectionRetryCount++;

    if (this.connectionRetryCount >= MAX_CONNECTION_RETRY_ATTEMPTS) {
      if (this.options.log) {
        console.log(`SignalR: Max connection retry attempts (${MAX_CONNECTION_RETRY_ATTEMPTS}) exceeded, stopping`);
      }
      return;
    }

    if (!this.authenticationFailed) {
      const retryDelay = Math.min(30000, this.connectionRetryCount * 5000);
      if (this.options.log) {
        console.log(`SignalR: Retrying connection in ${retryDelay}ms (attempt ${this.connectionRetryCount}/${MAX_CONNECTION_RETRY_ATTEMPTS})`);
      }
      setTimeout(() => this._initialize(), retryDelay);
    }
  }

  // ---------------------------------------------------------------------------
  // Method Listening (Private)
  // ---------------------------------------------------------------------------

  /**
   * Listen on all pending methods after connection is established
   */
  private listenOnPendingMethods(): void {
    this.pendingMethods.forEach(method => {
      this.listened.push(method);
      this.handleListenMethod(method);
    });
    this.pendingMethods = [];
  }

  /**
   * Setup listener for a specific method
   */
  private handleListenMethod(method: string): void {
    console.log(`Handle listen on method: ${method}`);
    this.socket.on(method, (name: any, data: any) => {
      if (this.options.log) console.log({ type: "receive", method, name, data });

      if (method === SOCKET_METHOD.DATA_CHANGE && name?.length > 0) {
        const ignoreSkipThisRowItem = name.filter((item: any) => !item?.SkipThisRow);
        if (ignoreSkipThisRowItem?.length === 0) return;
        name = ignoreSkipThisRowItem;
      }

      this.emit(method, { name, data });
    });
  }

  // ---------------------------------------------------------------------------
  // Authentication & Token Management (Private)
  // ---------------------------------------------------------------------------

  /**
   * Get the latest access token from localStorage
   * This ensures we always use the most recent token (e.g., after axios-helper refreshes it)
   */
  private getLatestAccessToken(): string {
    // Primary source: OAuth2 storage
    const auth2Json = localStorage.getItem(LOCALSTORAGE_AUTH2);
    if (auth2Json) {
      try {
        const auth2: Oauthorization2Response = JSON.parse(auth2Json);
        if (auth2?.data?.oauth2?.accessToken) {
          return auth2.data.oauth2.accessToken;
        }
      } catch (e) {
        // Ignore parse error
      }
    }

    // Fallback: User session
    const userSessionJson = localStorage.getItem(LOCALSTORAGE_USERSSION);
    if (userSessionJson) {
      try {
        const userSession: AuthVerified = JSON.parse(userSessionJson);
        if (userSession?.Token) {
          return userSession.Token;
        }
      } catch (e) {
        // Ignore parse error
      }
    }

    // Final fallback: options
    return this.options?.accessToken || "";
  }

  /**
   * Handle authentication error (401) by attempting to refresh the token
   * If refresh fails after MAX_AUTH_RETRY_ATTEMPTS, redirect to login
   */
  private async handleAuthenticationError(): Promise<void> {
    if (this.isRefreshingToken) {
      if (this.options.log) console.log("SignalR: Token refresh already in progress, skipping");
      return;
    }

    // OMSEVEN-10852: inside the WinApp only the WinApp refreshes; it pushes the new token into
    // localStorage, where accessTokenFactory reads it. Reconnect with that, and never clear the
    // session: every WinApp webview shares this localStorage.
    if (isWinAppWebview()) {
      if (this.authRetryCount >= MAX_AUTH_RETRY_ATTEMPTS) {
        this.authenticationFailed = true;
        return;
      }
      this.authRetryCount++;
      await this.delay(AUTH_RETRY_DELAY_MS);
      const reconnected = await this._initialize();
      if (reconnected) this.emit("reconnect");
      return;
    }

    if (this.authRetryCount >= MAX_AUTH_RETRY_ATTEMPTS) {
      if (this.options.log) {
        console.log(`SignalR: Max auth retry attempts (${MAX_AUTH_RETRY_ATTEMPTS}) exceeded, redirecting to login`);
      }
      this.authenticationFailed = true;
      this.clearAuthDataAndRedirectToLogin();
      return;
    }

    this.authRetryCount++;
    this.isRefreshingToken = true;

    if (this.options.log) {
      console.log(`SignalR: Attempting token refresh (attempt ${this.authRetryCount}/${MAX_AUTH_RETRY_ATTEMPTS})`);
    }

    try {
      const newToken = await this.refreshAccessToken();

      if (!newToken || newToken.data?.error || newToken.status !== 200) {
        throw new Error("Token refresh failed: Invalid response");
      }

      this.updateStoredTokens(newToken.data);
      this.options.accessToken = newToken.data.accessToken;

      if (this.options.log) console.log("SignalR: Token refreshed successfully, reconnecting...");

      this.isRefreshingToken = false;
      await this.delay(AUTH_RETRY_DELAY_MS);
      const reconnected = await this._initialize();
      if (reconnected) {
        this.emit("reconnect");
      }

    } catch (error) {
      if (this.options.log) console.log("SignalR: Token refresh failed", error);
      this.isRefreshingToken = false;

      if (this.authRetryCount < MAX_AUTH_RETRY_ATTEMPTS) {
        await this.delay(AUTH_RETRY_DELAY_MS);
        await this.handleAuthenticationError();
      } else {
        this.authenticationFailed = true;
        this.clearAuthDataAndRedirectToLogin();
      }
    }
  }

  /**
   * Refresh the access token using the refresh token
   */
  private async refreshAccessToken(): Promise<any> {
    const currentUserSession = localStorage.getItem(LOCALSTORAGE_USERSSION);
    if (!currentUserSession) {
      throw new Error("No user session found");
    }

    const userSession: AuthVerified = JSON.parse(currentUserSession);
    if (!userSession.RefreshToken) {
      throw new Error("No refresh token available");
    }

    const instance = axios.create({
      baseURL: SERVICE_LINKS.AUTH_URL,
      headers: {
        [APICONFIG_HEADER_AUTHORIZATION]: "Bearer " + userSession.Token,
        [APICONFIG_HEADER_SESSIONID]: userSession.SessionId || ''
      }
    });

    if (this.options.log) console.log("SignalR: Calling refresh token endpoint");

    return instance.get<{ [key: string]: any }>(
      `/auth/web/refresh/${userSession.RefreshToken}`
    );
  }

  /**
   * Update all stored tokens with the new token data
   */
  private updateStoredTokens(tokenData: any): void {
    // Calculate token expiration
    const now = new Date();
    const expiresIn = parseInt(tokenData.expiresIn) || 3600;
    tokenData.accessTokenExpiration = new Date(now.getTime() + ((expiresIn - 60) * 1000));

    // Update user session
    const currentUserSession = localStorage.getItem(LOCALSTORAGE_USERSSION);
    if (currentUserSession) {
      const userSession: AuthVerified = JSON.parse(currentUserSession);
      userSession.Token = tokenData.accessToken;
      userSession.RefreshToken = tokenData.refreshToken;
      localStorage.setItem(LOCALSTORAGE_USERSSION, JSON.stringify(userSession));
    }

    // Update OAuth2 storage
    const auth2Json = localStorage.getItem(LOCALSTORAGE_AUTH2);
    if (auth2Json) {
      const auth2: Oauthorization2Response = JSON.parse(auth2Json);
      auth2.oauth2 = tokenData;
      auth2.data.oauth2 = tokenData;
      localStorage.setItem(LOCALSTORAGE_AUTH2, JSON.stringify(auth2));
    }

    // Update auth storage
    const authJson = localStorage.getItem(LOCALSTORAGE_AUTH);
    if (authJson) {
      const auth = JSON.parse(authJson);
      auth.token = tokenData.accessToken;
      localStorage.setItem(LOCALSTORAGE_AUTH, JSON.stringify(auth));
    }

    // Update axios default headers
    axios.defaults.headers.common[APICONFIG_HEADER_AUTHORIZATION] = "Bearer " + tokenData.accessToken;

    EventBus.$emit(EVENT_BUS.AUTH.STATE_CHANGED);
  }

  /**
   * Clear authentication data and redirect to login page
   */
  private clearAuthDataAndRedirectToLogin(): void {
    EventBus.$emit(EVENT_BUS.AUTH.SIGNED_OUT, "session-expired");
    if (this.options.log) console.log("SignalR: Clearing auth data and redirecting to login");

    // Clear auth storage
    localStorage.removeItem("auth");
    localStorage.removeItem("AuthorizationData");
    localStorage.removeItem("userssion");
    LocalforageWrapper.clear();

    // Redirect to login
    const webappLayout = store.state.common?.webappLayoutMode;
    if (webappLayout === WEBAPP_LAYOUT.DEFAULT) {
      const authSettings = JSON.parse(localStorage.getItem("AuthorizationSettingData") || "{}");
      const loginUrlEl = document.querySelector("meta[name='Login-Url']");

      if (loginUrlEl) {
        location.href = loginUrlEl.getAttribute("content") || "";
      } else if (authSettings.LoginUrl) {
        location.href = authSettings.LoginUrl;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Utility Methods (Private)
  // ---------------------------------------------------------------------------

  /**
   * Helper function to delay execution
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// ============================================================================
// Plugin Registration
// ============================================================================

if (!SignalR) {
  throw new Error("[Vue-SignalR] cannot locate signalr-client");
}

const SignalRPlugin = {
  install(Vue: typeof _Vue, options?: any | {}) {
    if (!options || !options.url) {
      throw new Error("[Vue-SignalR] cannot locate connection");
    }

    let url = options.url;
    let environment = "";
    let customerName = "";

    const authJson = localStorage.AuthorizationData;

    if (authJson != null) {
      const auth2: Oauthorization2Response = JSON.parse(authJson);
      environment = auth2.environment;

      if (auth2.data != null && auth2.data.information != null) {
        customerName = auth2.data.information.customerName;
      }
    }

    if (options.urlTest && customerName === "ImmoVietNam" && (environment === "dev" || environment === "staging")) {
      url = options.urlTest;
    }

    const Socket = new SocketConnection(url);
    Object.defineProperties(Vue.prototype, {
      $socket: {
        get() {
          return Socket;
        }
      }
    });
  }
};

export default SignalRPlugin;

// ============================================================================
// Type Declarations
// ============================================================================

declare module 'vue/types/vue' {
  interface Vue {
    $socket: ISocketConnection;
  }
}
